import React, { useEffect, useRef, useState } from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { getData } from '../../Services/Services';
import { getOpenTicketsCount } from '../../Services/EndPoints';
import './ToastNotification.css';
 
const ToastNotification = ({ userId, onNotificationReceived }) => {

    
    const [notifications, setNotifications] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [wsEndpoint, setWsEndpoint] = useState(null);
    const clientRef = useRef(null);
 
    // ✅ FIXED: Direct backend connection for WebSocket (not through React dev server)
    const getWebSocketEndpoint = () => {
        try {
            const contextPath = process.env.REACT_APP_CORE_CONTEXT_PATH_NAME;
           
            if (!contextPath) {
                console.error('REACT_APP_CORE_CONTEXT_PATH_NAME is not defined');
                throw new Error('Missing context path environment variable');
            }
 
            const hostname = window.location.hostname;
            const protocol = window.location.protocol;
           
            let sockjsUrl;
           
            // ✅ LOCALHOST: Always use backend port 7080 (not React dev server port)
            if (hostname === 'localhost' || hostname === '127.0.0.1') {
                sockjsUrl = `http://localhost:7080/${contextPath}/ws`;
            }
            // ✅ UAT/PRODUCTION: Use same domain but check if needs context path
            else {
                // In production, your backend might be on same domain with context path
                sockjsUrl = `${protocol}//${hostname}/${contextPath}/ws`;
            }
           
            console.log('🔗 WebSocket URL constructed:', {
                environment: hostname === 'localhost' ? 'LOCAL' : 'PRODUCTION',
                hostname: hostname,
                protocol: protocol,
                contextPath: contextPath,
                fullUrl: sockjsUrl
            });
           
            return sockjsUrl;
        } catch (error) {
            console.error("Error constructing WebSocket endpoint:", error);
           
            // ✅ FALLBACK: Always direct to backend port in localhost
            const contextPath = process.env.REACT_APP_CORE_CONTEXT_PATH_NAME || 'iccc-service';
            const hostname = window.location.hostname;
           
            if (hostname === 'localhost' || hostname === '127.0.0.1') {
                return `http://localhost:7080/${contextPath}/ws`;
            } else {
                return `${window.location.protocol}//${hostname}/${contextPath}/ws`;
            }
        }
    };
 
    // ✅ Function to fetch notification count using getData service
    const fetchNotificationCount = async () => {
        try {
            const response = await getData(getOpenTicketsCount);
            if (onNotificationReceived) {
                onNotificationReceived(response);
            }
            return response;
        } catch (error) {
            console.error("Error fetching notification count:", error);
            if (onNotificationReceived) {
                onNotificationReceived(0);
            }
            return 0;
        }
    };
 
    useEffect(() => {
        if (!userId) {
            console.error('User ID not found');
            return;
        }
 
        const initializeWebSocket = async () => {
            console.log('🔌 Initializing WebSocket connection for user:', userId);
 
            try {
                // ✅ Get direct backend WebSocket URL
                const httpUrl = getWebSocketEndpoint();
                setWsEndpoint(httpUrl);
               
                console.log('🔌 Connecting directly to backend SockJS endpoint:', httpUrl);
 
                const stompClient = new Client({
                    // ✅ SockJS factory with direct backend URL
                    webSocketFactory: () => new SockJS(httpUrl),
                   
                    connectHeaders: {
                        // ✅ Add auth header if your WebSocket requires authentication
                        // Authorization: 'Bearer ' + localStorage.getItem("auth_token")
                    },
                   
                    debug: function (str) {
                        if (process.env.NODE_ENV === 'development') {
                            console.log('STOMP:', str);
                        }
                    },
                   
                    reconnectDelay: 5000,
                    heartbeatIncoming: 4000,
                    heartbeatOutgoing: 4000,
                   
                    onConnect: (frame) => {
                        console.log('✅ Connected to WebSocket via SockJS!');
                        console.log('🎯 Backend connection established on:', httpUrl);
                        setIsConnected(true);
                       
                        stompClient.subscribe(
                            `/topic/user/${userId}/notifications`,
                            (message) => {
                                const notification = JSON.parse(message.body);
                                console.log('📩 Received notification:', notification);
                               
                                const notificationWithId = {
                                    ...notification,
                                    id: Date.now() + Math.random()
                                };
                               
                                setNotifications(prev => [...prev, notificationWithId]);
                               
                                fetchNotificationCount();
                               
                                setTimeout(() => {
                                    removeNotification(notificationWithId.id);
                                }, 15000);
                               
                                showBrowserNotification(notification);
                            }
                        );
                       
                        console.log('✅ Subscribed to: /topic/user/' + userId + '/notifications');
                    },
                   
                    onStompError: (frame) => {
                        console.error('❌ STOMP error:', frame.headers['message']);
                        console.error('Error details:', frame.body);
                        console.error('❌ Check if backend is running on port 7080');
                        setIsConnected(false);
                    },
                   
                    onWebSocketError: (error) => {
                        console.error('❌ WebSocket error:', error);
                        console.error('❌ Make sure backend server is running on localhost:7080');
                        setIsConnected(false);
                    },
                   
                    onDisconnect: () => {
                        console.log('🔌 Disconnected from WebSocket');
                        setIsConnected(false);
                    }
                });
 
                stompClient.activate();
                clientRef.current = stompClient;
 
                // ✅ Initial fetch using getData
                await fetchNotificationCount();
 
            } catch (error) {
                console.error('Error initializing WebSocket:', error);
                setIsConnected(false);
            }
        };
 
        initializeWebSocket();
 
        return () => {
            if (clientRef.current) {
                clientRef.current.deactivate();
                clientRef.current = null;
                console.log('🔌 Deactivated WebSocket client');
                setIsConnected(false);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);
 
    useEffect(() => {
        return () => {
            setNotifications([]);
        };
    }, []);
 
    const showBrowserNotification = (notification) => {
        if (Notification.permission === 'granted') {
            new Notification('New Grievance Notification', {
                body: notification.message,
                icon: '/logo192.png',
                badge: '/logo192.png'
            });
        } else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    new Notification('New Grievance Notification', {
                        body: notification.message,
                        icon: '/logo192.png'
                    });
                }
            });
        }
    };
 
    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
    };
 
    const getNotificationIcon = (type) => {
    switch(type) {
        case 'TICKET_CREATED':
            return '🎫';
        case 'TICKET_ASSIGNED':
            return '👤';
        case 'TICKET_UPDATED':
            return '📝';
        case 'TICKET_IN_PROGRESS':
            return '⏳';
        case 'TICKET_RESOLVED':
            return '✅';
        case 'TICKET_CLOSED':
            return '🔒';
        case 'TICKET_REOPENED':
            return '🔓';
        case 'TICKET_ESCALATED':
            return '⚠️';
        case 'TICKET_ON_HOLD':
            return '⏸️';
        case 'TICKET_REJECTED':
            return '❌';
        case 'COMMENT_ADDED':
            return '💬';
        case 'ATTACHMENT_ADDED':
            return '📎';
        case 'TICKET_TRANSFERRED':
            return '🔁';
        case 'VEHICLE_ACCIDENT':
            return '💥🚗';
        case 'UNKNOWN_PERSON_ALERT':
            return '👤⚠️';
        default:
            return '📢';
    }
};
 
 
    const renderConnectionStatus = () => {
        const contextPath = process.env.REACT_APP_CORE_CONTEXT_PATH_NAME;
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
       
        if (!isConnected && wsEndpoint) {
            return (
                <></>
                // <div className="connection-status disconnected">
                //     <span>🔴 Disconnected from {wsEndpoint}</span>
                //     <small>Context: {contextPath}</small>
                //     {isLocal && <small>🚀 Direct backend connection (port 7080)</small>}
                // </div>
            );
        } else if (isConnected && wsEndpoint) {
            return (
                <div className="connection-status connected">
                    <span>🟢 Connected to {wsEndpoint}</span>
                    <small>Context: {contextPath}</small>
                    {isLocal && <small>🚀 Direct backend connection (port 7080)</small>}
                </div>
            );
        }
        return null;
    };
 
    return (
        <>
            {process.env.NODE_ENV === 'development' && renderConnectionStatus()}
           
              <div className="toast-container">
                {notifications.map((notification) => (
                    <div
                        key={notification.id}
                        className="toast-notification"
                    // Remove onClick from here to prevent conflicts
                    >
                        <button
                            className="toast-close"
                            onClick={(e) => {
                                e.preventDefault(); // Prevent default button behavior
                                e.stopPropagation(); // Stop event bubbling
                                removeNotification(notification.id);
                            }}
                            type="button" // Explicitly set button type
                            aria-label="Close notification" // Accessibility
                        >
                            ×
                        </button>
                        <div className="toast-icon">
                            {getNotificationIcon(notification.type)}
                        </div>
                        <div className="toast-content">
                            <div className="toast-title">{notification.grievanceId}</div>
                            <div className="toast-message">{notification.message}</div>
                            {/* <div className="toast-time">
                                {new Date(notification.timestamp).toLocaleTimeString()}
                            </div> */}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};
 
export default ToastNotification;
 
 